import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import store from '../store';
import {setExplainActive} from '../slices/statusSlice';
import close from '../../icons/close';
import {decision} from '../slices/decisionsSlice';

@customElement('explain-view')
export class ExplainView extends connect(store)(LitElement) {

	static styles = css`
	  :host {
		z-index: 20;
	  }

	  @keyframes reveal {
		from {
		  opacity: 0;
		}

		to {
		  opacity: 100%;
		}
	  }
	  @keyframes slide {
		from {
		  transform: translateX(100px);
		}

		to {
		  transform: translateX(0px);
		}
	  }

	  .modal {
		position: absolute;
		height: 100%;
		width: 100%;
		left: 0;
		top: 0;
		display: flex;
		justify-content: flex-end;
		background-color: rgba(0, 0, 0, 0.75);
		opacity: 100%;
		animation: reveal 0.25s ease-out normal forwards;
	  }

	  p {
		font: var(--font-light);
	  }

	  .close {
		flex-basis: 0;
		color: var(--color-black);
		background-color: transparent;
		border: none;
		cursor: pointer;
		height: 20px;
	  }

	  .close svg {
		pointer-events: none;
	  }

	  .close svg * {
		stroke: currentColor;
	  }

	  .panel {
		padding: var(--large-padding);
		background-color: var(--color-grey1);
		width: 50vw;
		animation: slide 0.25s ease-out normal forwards;
		display: flex;
		flex-direction: row;
		gap: var(--large-padding);
	  }

	  .content {
		overflow-y: auto;
		height: 100%;
		flex-basis: 67%;
		font: var(--font-light);

	  }


	  .desiredFeature {
		flex-basis: 50%;
	  }

	  .feature {
		font: var(--font-bold);
		margin-bottom: var(--very-small-padding);
	  }

	  .value {
		font: var(--font-light);
		margin-bottom: var(--small-padding);
	  }

	  .desiredValue {
		font: var(--font-light);
		padding: 0.5em 0.5em;
		border: var(--border-desired);
		margin-bottom: var(--small-padding);
	  }

	  .conflictingValue {
		font: var(--font-light);
		padding: 0.5em 0.5em;
		border: var(--border-conflict);
		margin-bottom: var(--small-padding);
	  }

	  .buttons {
		display: flex;
		gap: var(--small-padding);
	  }

	  button {
		font-family: var(--font-family-light);
		font-size: var(--font-size-widget);
		color: var(--color-black);
		height: var(--height-widget);
		border: 1px solid var(--color-black);
		text-transform: uppercase;
		letter-spacing: 1px;
		transition: background-color 0.4s, color 0.4s;
		background-color: transparent;
		cursor: pointer;
		line-height: 0;
		flex-basis: 50%
	  }

	  button.ok {
		background-color: var(--color-black);
		color: var(--color-white);
	  }

	  @media (max-width: 768px) {
		.panel {
		  width: 100vw;
		  flex-direction: column;
		}

		.content {
		  flex-basis: 100%;
		}

		.title {
		  flex-basis: 0;
		}

		.close {
		  position: absolute;
		  top: var(--large-padding);
		  right: var(--large-padding);
		}
	  }

	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.status = state.status;
		this.explainActive = this.status.explainActive;
		this.desiredFeature = this.status.explanations?.desiredFeature;
		this.causes = this.status.explanations?.causes;
		this.lang = state.status.lang;
	}

	connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 27: // ESC
				if (this.explainActive) {
					this.closeexplain();
				}
				break;
		}
	};

	cancel() {
		store.dispatch(setExplainActive(
			{
				explainActive: false,
			}
		));
	}

	async apply() {
		// remove the causes
		await this.causes.forEach(cause => {
			store.dispatch(decision(
				{
					variableId: cause.split('.')[0],
					featureValueId: cause.split('.')[1],
					state: 'Undefined'
				}
			)).unwrap();
		});
		setTimeout((desiredFeature) => {
				store.dispatch(decision(
					{
						variableId: desiredFeature.variableId,
						featureValueId: desiredFeature.featureValueId,
						state: 'Included'
					}
				));
			}, 400, this.desiredFeature
		);
		// close modal
		store.dispatch(setExplainActive(
			{
				explainActive: false,
			}
		));
	}

	closeexplain(e) {
		const targetId = e.target.getAttribute('id');
		if (targetId === 'modal' || targetId === 'close') {
			store.dispatch(setExplainActive(
				{
					explainActive: false,
				}
			));
		}
	}

	render() {
		return this.explainActive ? html`
			<div id="modal" class="modal" @click="${this.closeexplain}">
				<div class="panel">
					${when(this.desiredFeature !== null, () => html`
						<div class="desiredFeature">
							<div class="feature">
								<trans-label slot="label" key=${this.desiredFeature.variableId} context="F"></trans-label>
							</div>
							<div class="desiredValue">
								<trans-label slot="label" key=${this.desiredFeature.variableId} value=${this.desiredFeature.featureValueId}
											 context="FV"></trans-label>
							</div>
						</div>
					`)}
					${when(this.causes !== null, () => html`
						<div class="content">
							${repeat(this.causes, (cause) => html`
								<div>
									<div class="feature">
										<trans-label slot="label" key=${cause.split('.')[0]} context="F"></trans-label>
									</div>
									<div class="conflictingValue">
										<trans-label slot="value" key=${cause.split('.')[0]} value=${cause.split('.')[1]} context="FV"></trans-label>
									</div>
								</div>
							`)}
							<p>
								${msg('The desired feature is not possible with your previous decisions. Do you want to deactivate these choices to proceed?')}
							</p>
							<div class="buttons">
								<button class="cancel" @click="${this.cancel}">
									${msg('Cancel')}
								</button>
								<button class="ok" @click="${this.apply}">
									${msg('Ok')}
								</button>
							</div>
						</div>
					`)}
					<button id="close" @click="${this.closeexplain}" class="close">
						${close}
					</button>
				</div>
			</div>
		` : null;
	}
}
